import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ResponsiveCarousel, CarouselItem } from '@lux-sandbox/carousel';
import { getCampaignImage } from '@browsing/campaign';
import { ErrorBoundary } from '@browsing/components';
import { TitlePreset } from '@lux/title';
import { Button, ButtonColor, ButtonType } from '@lux/buttons';
import { PlusLogoPill, PlusTierPill, Size } from '@lux/plus-badge';
import { Translate, TranslateScope } from '@pegasus/translation';
import { LoyaltyStatus } from '@pegasus/plus';

import {
    getCategoryTabId,
    getEAOpenCampaignsForCarousel,
    getTabs,
} from '@client/redux/modules/mylounge';
import { useNavigate } from 'react-router-dom';
import { trackSelectContent, trackSelectPromotion, trackViewPromotion } from '@browsing/tracking';

import { CampaignCardMini } from '../../campaigns/components/campaign-card-mini';
import { useLoyalty } from '../../hooks/use-loyalty';

import * as S from './ea-carousel.styles';

export const EACarousel: React.FC = () => {
    const earlyAccessCampaigns = useSelector(getEAOpenCampaignsForCarousel);
    const tabs = useSelector(getTabs);
    const loyalty = useLoyalty();
    const isLoyaltyEligible = loyalty.summary.status === LoyaltyStatus.Eligible;
    const tier = loyalty.summary.tier || 'TIER_1';
    const categoryTabId = useSelector(getCategoryTabId);
    const navigate = useNavigate();

    // Create memoized handlers for campaign interactions
    const handleCampaignClick = useCallback(
        (campaignId: string) => {
            trackSelectPromotion({
                element: 'early_access_carousel',
                creativeName: 'early_access_carousel',
                item: {
                    campaignIdentifier: campaignId,
                    earlyAccessItem: 1,
                    categoryId: categoryTabId,
                },
            });
        },
        [categoryTabId],
    );

    const handleCampaignView = useCallback(
        (campaignId: string, index: number) => {
            trackViewPromotion(
                {
                    element: 'early_access_carousel',
                    creativeName: 'early_access_carousel',
                    itemListName: 'early_access_carousel',
                    items: [
                        {
                            campaignIdentifier: campaignId,
                            categoryId: categoryTabId,
                            index,
                        },
                    ],
                },
                {
                    customMapper: () => ({
                        early_access_item: 1,
                    }),
                },
            );
        },
        [categoryTabId],
    );

    // Create stable handlers for each campaign to avoid creating new functions on every render
    const campaignClickHandlers = useMemo(() => {
        const handlers: Record<string, () => void> = {};
        earlyAccessCampaigns?.forEach(campaign => {
            handlers[campaign.campaignId] = () => handleCampaignClick(campaign.campaignId);
        });
        return handlers;
    }, [earlyAccessCampaigns, handleCampaignClick]);

    const campaignViewHandlers = useMemo(() => {
        const handlers: Record<string, () => void> = {};
        earlyAccessCampaigns?.forEach((campaign, index) => {
            handlers[campaign.campaignId] = () => handleCampaignView(campaign.campaignId, index);
        });
        return handlers;
    }, [earlyAccessCampaigns, handleCampaignView]);

    if (!earlyAccessCampaigns || earlyAccessCampaigns.length === 0) {
        return null;
    }

    // Find the tab with PLUS_OFFER_EVENT and construct the URL
    const plusOfferTab = tabs.find(tab => tab.event === 'PLUS_OFFER_EVENT');
    const plusOffersUrl = plusOfferTab ? `/event/${plusOfferTab.id}` : '/';

    const handleSeeAllClick = () => {
        trackSelectContent('early_access_carousel', 'click', { contentId: 'see_all_offers' });
        navigate(plusOffersUrl);
    };

    const handleSeeAllCardClick = () => {
        trackSelectContent('early_access_carousel', 'click', { contentId: 'see_all' });
        navigate(plusOffersUrl);
    };

    // Get the last campaign's image for the See All card background
    const lastCampaign = earlyAccessCampaigns[earlyAccessCampaigns.length - 1];
    const lastCampaignImage = lastCampaign ? getCampaignImage(lastCampaign, '1x2') : null;

    return (
        <ErrorBoundary componentName="EACAROUSEL">
            <TranslateScope path="plus.loyalty.eacarousel">
                <S.EACarouselContainer>
                    <S.DetailsWrapper>
                        <S.TierPillWrapper>
                            <TranslateScope path="plus">
                                {isLoyaltyEligible ? (
                                    <>
                                        <PlusLogoPill dynamicFontSize size={Size.M} />
                                        <S.EarlyAccessLabel>
                                            <Translate message="plusEarlyAccess" />
                                        </S.EarlyAccessLabel>
                                    </>
                                ) : (
                                    <>
                                        <PlusTierPill tier={tier} size={Size.M}>
                                            <Translate message={`loyalty.tier.${tier}`} />
                                        </PlusTierPill>
                                        <S.EarlyAccessLabel>
                                            <Translate message="plusEarlyAccess" />
                                        </S.EarlyAccessLabel>
                                    </>
                                )}
                            </TranslateScope>
                        </S.TierPillWrapper>
                        <S.ContentWrapper>
                            <S.StyledHeadline
                                id="ea-carousel-title"
                                preset={TitlePreset.S}
                                presetLg={TitlePreset.M}
                            >
                                <Translate message="openTitle" />
                            </S.StyledHeadline>
                            <S.SeeAllButton
                                buttonType={ButtonType.tertiary}
                                href={plusOffersUrl}
                                onClick={handleSeeAllClick}
                            >
                                <Translate message="openCta" />
                            </S.SeeAllButton>
                        </S.ContentWrapper>
                    </S.DetailsWrapper>
                    <S.EACarouselWrapper>
                        <ResponsiveCarousel
                            aria-labelledby="ea-carousel-title"
                            visibleItems={1.5}
                            visibleItemsSm={2}
                            visibleItemsMd={2.3}
                            visibleItemsLg={2.3}
                            previousSlideLabel=""
                            nextSlideLabel=""
                            withOverflow
                        >
                            {earlyAccessCampaigns.map((campaign, index) => {
                                const media = getCampaignImage(campaign, '1x1');

                                return (
                                    <TranslateScope path="myLounge">
                                        <CarouselItem key={campaign.campaignId}>
                                            <CampaignCardMini
                                                campaign={campaign}
                                                media={media}
                                                hideTimeLeft
                                                isPlusVariant
                                                onClick={campaignClickHandlers[campaign.campaignId]}
                                                onView={campaignViewHandlers[campaign.campaignId]}
                                            />
                                        </CarouselItem>
                                    </TranslateScope>
                                );
                            })}

                            <CarouselItem>
                                <S.LastSeeAll onClick={handleSeeAllCardClick}>
                                    {lastCampaignImage && (
                                        <>
                                            <S.LastSeeAllImage
                                                src={lastCampaignImage.url}
                                                aria-hidden="true"
                                            />
                                            <S.LastSeeAllOverlay />
                                        </>
                                    )}
                                    <S.LastSeeAllButton>
                                        <Button
                                            as="span"
                                            buttonType={ButtonType.tertiary}
                                            color={ButtonColor.dark}
                                        >
                                            <Translate message="seeAllDeals" />
                                        </Button>
                                    </S.LastSeeAllButton>
                                </S.LastSeeAll>
                            </CarouselItem>
                        </ResponsiveCarousel>
                    </S.EACarouselWrapper>
                </S.EACarouselContainer>
            </TranslateScope>
        </ErrorBoundary>
    );
};
